import { useEffect, useState } from "react";
import { docsClient, Document } from "@/lib/supabase";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, Tag as TagIcon, FileText } from "lucide-react";

interface TagWithCount {
  tag: string;
  count: number;
  documents: Document[];
}

export default function TagsPage() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [tags, setTags] = useState<TagWithCount[]>([]);
  const [filteredTags, setFilteredTags] = useState<TagWithCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [totalTags, setTotalTags] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    loadDocuments();
  }, []);

  useEffect(() => {
    filterTags();
  }, [searchQuery, tags]);

  const loadDocuments = async () => {
    try {
      // Get accurate totals from RPC function (bypasses pagination limits)
      const { data: stats, error: statsError } = await (
        docsClient.rpc as (
          name: string,
          params?: Record<string, unknown>
        ) => Promise<{
          data: { total_documents: number; total_tags: number }[] | null;
          error: unknown | null;
        }>
      )("get_user_stats");

      if (statsError) {
        console.error("Error loading stats:", statsError);
      } else if (stats && stats.length > 0) {
        setTotalDocuments(stats[0].total_documents);
        setTotalTags(stats[0].total_tags);
      }

      // Use optimized RPC function for tag counts instead of loading all documents
      const { data: tagCounts, error: tagError } = await (
        docsClient.rpc as (
          name: string,
          params?: Record<string, unknown>
        ) => Promise<{ data: { tag: string; count: number }[] | null; error: unknown | null }>
      )("get_tag_counts");

      if (tagError) throw tagError;

      // Only load document details when we have tag counts
      if (tagCounts && tagCounts.length > 0) {
        // Load documents for tag details (needed for click navigation)
        // This is much faster with server-side indexes
        // Note: Using range(0, 9999) to bypass Supabase's 1000 row default limit
        const { data: docs, error: docsError } = await supabase
          .from("documents")
          .select("id, title, url, tags, created_at")
          .order("created_at", { ascending: false })
          .range(0, 9999); // Load up to 10,000 documents

        if (docsError) throw docsError;

        setDocuments(docs || []);
        processTags(tagCounts, docs || []);
      } else {
        setTags([]);
        setFilteredTags([]);
      }
    } catch (error) {
      console.error("Error loading tags:", error);
    } finally {
      setLoading(false);
    }
  };

  const processTags = (tagCounts: { tag: string; count: number }[], docs: Document[]) => {
    // Build document lookup map for each tag
    const tagMap = new Map<string, Document[]>();

    docs.forEach((doc) => {
      doc.tags.forEach((tag) => {
        if (!tagMap.has(tag)) {
          tagMap.set(tag, []);
        }
        tagMap.get(tag)?.push(doc);
      });
    });

    // Use server-provided counts (already sorted by count DESC)
    const tagsWithCounts: TagWithCount[] = tagCounts.map(({ tag, count }) => ({
      tag,
      count,
      documents: tagMap.get(tag) || [],
    }));

    setTags(tagsWithCounts);
    setFilteredTags(tagsWithCounts);
  };

  const filterTags = () => {
    if (!searchQuery) {
      setFilteredTags(tags);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = tags.filter((tagWithCount) => tagWithCount.tag.toLowerCase().includes(query));
    setFilteredTags(filtered);
  };

  const handleTagClick = (tag: string) => {
    // Navigate to documents page with tag filter
    navigate(`/documents?tag=${encodeURIComponent(tag)}`);
  };

  const getTotalDocuments = () => {
    // Use accurate total from RPC function, not documents.length which is capped at 10k
    return totalDocuments;
  };

  const getTotalTags = () => {
    // Use accurate total from RPC function, not tags.length which reflects loaded docs only
    return totalTags;
  };

  const getAverageTagsPerDocument = () => {
    if (documents.length === 0) return 0;
    const totalTagsCount = documents.reduce((sum, doc) => sum + doc.tags.length, 0);
    return (totalTagsCount / documents.length).toFixed(1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-foreground">Tags</h1>
        <p className="text-muted-foreground mt-2">Organize and browse your documentation by tags</p>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tags</CardTitle>
            <TagIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{getTotalTags()}</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Documents</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{getTotalDocuments()}</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Tags/Document</CardTitle>
            <TagIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{getAverageTagsPerDocument()}</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search tags..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Tags Grid */}
      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-32" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-4 w-24" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredTags.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <TagIcon className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">
              {searchQuery
                ? "No tags match your search"
                : "No tags yet. Add tags to your documents to organize them."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredTags.map(({ tag, count, documents: tagDocs }) => (
            <Card
              key={tag}
              className="cursor-pointer transition-all hover:shadow-lg hover:border-primary/50"
              onClick={() => handleTagClick(tag)}
            >
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <Badge variant="secondary" className="text-base font-semibold">
                    {tag}
                  </Badge>
                  <span className="text-sm font-normal text-muted-foreground">
                    {count} doc{count !== 1 ? "s" : ""}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  {tagDocs.slice(0, 3).map((doc) => (
                    <div key={doc.id} className="text-sm text-muted-foreground truncate">
                      • {doc.title || doc.url}
                    </div>
                  ))}
                  {count > 3 && (
                    <div className="text-sm text-muted-foreground/70">+ {count - 3} more...</div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
